import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { FaTiktok, FaWhatsapp } from 'react-icons/fa';
import logo from '../images/Party-Crafters_Logo.png';
import { SOCIAL } from '../lib/site';

// kind: 'route' for pages, 'section' for homepage anchors
const navItems = [
  { label: 'Home', to: '/', kind: 'route' },
  { label: 'Services', to: '#services', kind: 'section' },
  { label: 'Gallery', to: '/gallery', kind: 'route' },
  { label: 'Testimonials', to: '#testimonials', kind: 'section' },
  { label: 'FAQ', to: '#faq', kind: 'section' },
  { label: 'About Us', to: '/about', kind: 'route' },
];

// convert '#id' to a location object so it works from any route
const toHashLink = (hash) => ({ pathname: '/', hash });

export default function Header() {
  const [open, setOpen] = useState(false);
  const [elevated, setElevated] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [atTop, setAtTop] = useState(true);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      setElevated(y > 4);
      const nowAtTop = y < 12;
      setAtTop(nowAtTop);
      if (nowAtTop) setActiveSection('');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Observe homepage sections to highlight active anchor links
  useEffect(() => {
    if (location.pathname !== '/') return;

    const ids = navItems
      .filter((i) => i.kind === 'section')
      .map((i) => i.to.replace(/^\/?#/, '')); // supports '#id' or '/#id'

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const HEADER_GUESS = 64; // tweak if your header height changes
    const observer = new IntersectionObserver(
      (entries) => {
        let bestId = null;
        let bestRatio = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestId = entry.target.id;
          }
        });

        if (bestId) setActiveSection(bestId);
      },
      {
        rootMargin: `-${HEADER_GUESS + 6}px 0px -35% 0px`,
        threshold: [0.35, 0.6, 1],
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const linkClasses = (active) =>
    `text-sm font-medium transition-colors ${active ? 'text-pink-600' : 'text-gray-800 hover:text-orange-600'
    }`;

  const isHomeActive = () =>
    location.pathname === '/' && atTop && !activeSection;

  const sectionActive = (sectionId) =>
    location.pathname === '/' && !atTop && activeSection === sectionId;

  const triggerWhatsAppForm = (e) => {
    e.preventDefault();
    setOpen(false);
    window.dispatchEvent(new CustomEvent('open-whatsapp-form'));
  };

  // Helper to render a section link that highlights immediately on click
  const SectionLink = ({ id, children, className }) => (
    <Link
      to={toHashLink(`#${id}`)}
      className={className}
      onClick={() => setActiveSection(id)} // immediate highlight on click
    >
      {children}
    </Link>
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 ${elevated ? 'bg-white' : 'bg-white'
          }`}
        role="banner"
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-14 flex items-center justify-between">
            <Link to="/" aria-label="Home" className="inline-flex">
              <img 
              src={logo} 
              alt="Party Crafters Logo" 
              width="160"
              height="103"
              className="h-10 w-auto drop-shadow-none" 
              style={{ filter: 'none', boxShadow: 'none' }}
              />
            </Link>

            <nav className="hidden md:flex items-center gap-4 lg:gap-6" aria-label="Primary">
              {navItems.map((item) => {
                if (item.kind === 'route') {
                  return (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      end={item.to === '/'} // only end for home
                      className={({ isActive }) =>
                        linkClasses(item.to === '/' ? isHomeActive() : isActive)
                      }
                    >
                      {item.label}
                    </NavLink>
                  );
                }
                const id = item.to.replace(/^\/?#/, '');
                return (
                  <SectionLink
                    key={item.label}
                    id={id}
                    className={linkClasses(sectionActive(id))}
                  >
                    {item.label}
                  </SectionLink>
                );
              })}

              <div className="ml-2 flex items-center gap-2">
                <a
                  href={SOCIAL.instagram}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label="Instagram"
                  className="p-1 rounded hover:scale-110 transition-transform"
                  title="Instagram"
                >
                  <img src="/icons/instagram.svg" alt="Instagram" className="w-5 h-5" />
                </a>
                <a
                  href={SOCIAL.facebook}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label="Facebook"
                  className="p-1 rounded hover:scale-110 transition-transform"
                  title="Facebook"
                >
                  <img src="/icons/facebook.svg" alt="Facebook" className="w-6 h-6" />
                </a>
                <a
                  href={SOCIAL.tiktok}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  aria-label="TikTok"
                  className="p-1 rounded hover:scale-110 transition-transform"
                  title="TikTok"
                >
                  <FaTiktok size={21} className="text-gray-900" />
                </a>
                <button
                  type="button"
                  onClick={triggerWhatsAppForm}
                  aria-label="WhatsApp"
                  title="WhatsApp"
                  className="p-1 rounded hover:scale-110 transition-transform"
                >
                  <FaWhatsapp size={23} className="text-[#25D366]" />
                </button>
              </div>
            </nav>

            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-2"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        className={`md:hidden fixed top-14 left-0 right-0 z-40 bg-white border-t border-gray-100 transition-transform duration-200 ease-out ${open ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0 pointer-events-none'
          }`}
      >
        <nav className="px-4 py-3 flex flex-col gap-1" aria-label="Mobile">
          {navItems.map((item) => {
            if (item.kind === 'route') {
              return (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `block rounded-md px-3 py-2 ${linkClasses(item.to === '/' ? isHomeActive() : isActive)}`
                  }
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </NavLink>
              );
            }
            const id = item.to.replace(/^\/?#/, '');
            return (
              <SectionLink
                key={item.label}
                id={id}
                className={`block rounded-md px-3 py-2 ${linkClasses(sectionActive(id))}`}
              >
                {item.label}
              </SectionLink>
            );
          })}

          <div className="mt-2 flex items-center gap-3 px-2 pb-1">
            <a
              href={SOCIAL.instagram}
              target="_blank"
              rel="nofollow noopener noreferrer"
              aria-label="Instagram"
              className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-100"
              title="Instagram"
              onClick={() => setOpen(false)}
            >
              <img src="/icons/instagram.svg" alt="Instagram" className="w-5 h-5" />
            </a>
            <a
              href={SOCIAL.facebook}
              target="_blank"
              rel="nofollow noopener noreferrer"
              aria-label="Facebook"
              className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-100"
              title="Facebook"
              onClick={() => setOpen(false)}
            >
              <img src="/icons/facebook.svg" alt="Facebook" className="w-6 h-6" />
            </a>
            <a
              href={SOCIAL.tiktok}
              target="_blank"
              rel="nofollow noopener noreferrer"
              aria-label="TikTok"
              className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-100"
              title="TikTok"
              onClick={() => setOpen(false)}
            >
              <FaTiktok size={21} className="text-gray-900" />
            </a>
            <button
              type="button"
              onClick={(e) => { triggerWhatsAppForm(e); setOpen(false); }}
              aria-label="WhatsApp"
              title="WhatsApp"
              className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-100"
            >
              <FaWhatsapp size={23} className="text-[#25D366]" />
            </button>
          </div>
        </nav>
      </div>
    </>
  );
}
